import { Button } from '@/components/ui/button';
import { fetchShopOptionsCounting } from '@/lib/api/fetch-shop-options-counting';
import { checkCartStock } from '@/lib/check-cart-stock';
import { useAppDispatch } from '@/lib/client-hook/use-app-dispatch';
import { useDictionary } from '@/lib/context/locale-provider';
import { Languages } from '@/lib/dictionaries';
import { RootState } from '@/lib/store';
import { useAddCartMutation, useGenerateCartMutation } from '@/slice/cartApi';
import {
  setCartCurrency,
  setCartRedirectUrl,
  setCarts,
} from '@/slice/cartSlice';
import { setErrorMessage, setErrorRedirectUrl } from '@/slice/error';
import { deleteCart } from '@/slice/shopCartSlice';
import { useRouter } from 'next/navigation';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import '@/app/input.css';

function ShopCartBottom({
  lang,
  isEnded,
  isDonation,
  onMobileShow,
}: {
  lang: Languages;
  isEnded: boolean;
  isDonation: boolean;
  onMobileShow: () => void;
}) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const dict = useDictionary();
  const [addCart] = useAddCartMutation();
  const [generateCart] = useGenerateCartMutation();

  const shopId = useSelector((state: RootState) => state.shopCart.shopId);
  const cartItems = useSelector((state: RootState) => state.shopCart.items);
  const totalPrice = useSelector(
    (state: RootState) => state.shopCart.totalPrice,
  );

  const totalCount = useMemo(
    () =>
      dict!.shop.totalItems.replace(
        '{n}',
        cartItems.length > 0
          ? cartItems.reduce((prev, curr) => prev + curr.count, 0)
          : 0,
      ),
    [cartItems],
  );

  const handleGoCart = async () => {
    if (cartItems.length === 0) {
      onMobileShow();
      return;
    }
    try {
      const response = await addCart({
        shopId,
        items: cartItems,
      }).unwrap();
      if (response.status === 200) {
        router.push(`/${lang}/cart`);
      } else {
        dispatch(setErrorMessage(dict!.error.invalidAccess));
      }
    } catch (e: any) {
      dispatch(setErrorMessage(dict!.error.invalidAccess));
    }
  };

  const handleGoPay = async () => {
    if (cartItems.length === 0) {
      onMobileShow();
      return;
    }
    try {
      const response = await generateCart({
        shopId,
        items: cartItems,
      }).unwrap();
      if (response.status === 200) {
        const carts = [response.carts];

        const { hasSoldOut } = await checkCartStock({
          carts,
          fetchStock: fetchShopOptionsCounting,
          onSoldOut: ({ serial }) => {
            const soldOutItem = cartItems.find(item => item.serial === serial);
            if (soldOutItem) {
              dispatch(deleteCart(soldOutItem));
            }
          },
        });

        if (hasSoldOut) {
          dispatch(setErrorMessage(dict!.shop.error.soldOutRefresh));
        } else {
          localStorage.setItem('cart-redirect-url', `/${lang}/shop/${shopId}`);
          dispatch(setCarts(carts));
          dispatch(setCartRedirectUrl(`/${lang}/${shopId}`));
          dispatch(setCartCurrency(dict!.common.currency.replace('원', 'KRW')));
          router.replace(`/${lang}/pay`);
        }
      } else if (response.status === 401) {
        dispatch(setErrorRedirectUrl(`/${lang}/signin`));
        dispatch(setErrorMessage(dict!.error.pleaseLogin));
      } else {
        dispatch(setErrorMessage(dict!.error.invalidAccess));
      }
    } catch (e: any) {
      console.error(e.message);
    }
  };

  const totalFormattedPrice = useMemo(
    () =>
      totalPrice > 0
        ? `${totalPrice.toLocaleString('en-US')}${dict!.common.currency}`
        : `0${dict!.common.currency}`,
    [totalPrice],
  );

  return (
    <div className="flex flex-col p-[15px]  gap-[5px] relative shadow-[0px_-4px_10px_-5px_rgba(0,0,0,0.1)] bg-[#fff5f5]">
      <div className="flex flex-row justify-between">
        <p className="giftifan5 font-medium text-lg">{totalCount}</p>
        <p className="giftifan5 font-medium text-lg">{totalFormattedPrice}</p>
      </div>
      <div className="flex flex-row justify-between gap-[26px]">
        {isEnded ? (
          <Button
            variant="outline"
            className="flex-1 border-2 border-solid border-[#666666] text-[#666666] text-xl font-bold hover:text-[#666666] bg-[#F5F5F5] hover:bg-[#F5F5F5] break-word whitespace-normal h-auto"
          >
            {dict!.error.endSale}
          </Button>
        ) : (
          <>
            {!isDonation && (
              <Button
                variant="outline"
                className="flex-1 border-2 border-solid border-[#FF7395] giftifan4 text-xl font-bold hover:text-[#FF7395] hover:bg-white"
                onClick={handleGoCart}
              >
                {dict!.shop.shippingCart}
              </Button>
            )}
            <Button
              variant="outline"
              className="flex-1 text-white text-xl font-bold hover:text-white bg-[#FF7395] hover:bg-[#FF7395]"
              onClick={handleGoPay}
            >
              {isDonation ? dict!.active.participate : dict!.active.buy}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default ShopCartBottom;
